import { EMPTY_FILTERS } from "../api/types";
import type {
  CallLogEntry,
  Client,
  Contact,
  FollowedRole,
  GridUpload,
  ListRolesOptions,
  MatchedRolesPage,
  NewClient,
  RoleOptions,
  RolesPage,
  Project,
  ProjectUpdate,
  ProposedChange,
  ProposedFieldChange,
  ProposedRole,
  RoleWithProject,
  StatusOption,
  AssistantTurn,
  KnownContact,
  StatusTag,
} from "../api/types";
import { statusTags } from "../lib/project";
import type { DataSource } from "./source";

/**
 * Stub data for building screens before the endpoints exist.
 *
 * Projects, roles and the update feed are extracted from the three real
 * grids: WME Full Feature, IE Film and IE TV. Titles, studios, locations,
 * statuses, character descriptions, ages and crew are exactly as they appear
 * in the source. Regenerating is a re-run of the extractor, not an edit here.
 *
 * That means the awkward cases are real rather than invented. Titles carry
 * AKA and fka inline, several projects have no genre or no location, ages
 * arrive as a single number as often as a range, and American Psycho is in
 * here twice, once from each agency under a different title, unresolved.
 * candidate_count is the real appointment count from the IE grids, which is
 * why most roles show zero and a few show several.
 *
 * Clients are still invented, deliberately. The grids are agency project
 * grids and do not contain 1v1's roster, so using real actor names as 1v1
 * clients would be inventing a representation relationship rather than
 * using real data. Their filters are tuned against the real roles so the
 * match list is not empty.
 */

/** Matched dates are relative to now, not fixed.
 *
 * They were literals in August, which meant that by September nothing was
 * recent, so the New badges and the "since you last checked" banner stopped
 * rendering entirely. Both are prominent in the design and neither would have
 * appeared in a walkthrough. Relative dates keep the screen truthful whenever
 * it is opened. */
const daysAgo = (n: number): string =>
  new Date(Date.now() - n * 86_400_000).toISOString();

/** The update feed is the first thing on the dashboard, so the same reasoning
 *  applies to it and more sharply: a "Project Updates" card whose newest entry
 *  is a week old says nothing is happening. Hours rather than days, so the top
 *  of the feed always reads as recent. */
const hoursAgo = (n: number): string =>
  new Date(Date.now() - n * 3_600_000).toISOString();

/**
 * What each status tag is, mirroring app/lib/statuses.py on the API.
 *
 * Fixtures only. The real list comes from the API; this exists so a screen
 * developed against fixtures is grouped the same way as the live one, rather
 * than looking right here and wrong in front of somebody.
 */
const STATUS_KIND: Record<string, StatusOption["kind"]> = {
  development: "stage",
  "actively packaging": "stage",
  active: "stage",
  casting: "stage",
  "actively casting": "stage",
  done: "stage",
  new: "tracking",
  "long term tracking - features": "tracking",
  "long term tracking - tv": "tracking",
  features: "kind",
  pilot: "kind",
  "pilots & episodics": "kind",
  devlit: "desk",
  devtal: "desk",
  "high-end material": "desk",
  "incoming oda": "desk",
  oda: "desk",
  "oda - film": "desk",
  owa: "desk",
  "owa - television": "desk",
  package: "desk",
};

/** Declared order, so the picker reads the way the work runs. */
const STATUS_ORDER = Object.keys(STATUS_KIND);

/** A status cell as the API returns it: split, de-duplicated, classified. */
function classify(raw: string | null): StatusTag[] {
  const out: StatusTag[] = [];
  for (const label of statusTags(raw)) {
    if (out.some((t) => t.label === label)) continue;
    out.push({ label, kind: STATUS_KIND[label.toLowerCase()] ?? "unknown" });
  }
  return out;
}

/* Written without directors and producers, which the loop below fills from
 * the labelled contacts. Repeating them in twenty literals would be twenty
 * places for the two to disagree with the contacts they are derived from. */
const projects: Project[] = ([
  {
    id: "p1",
    title: "American Psycho Remake",
    project_type: "Feature Film",
    status_raw: "Features, New",
    canonical_stage: "development",
    studio: "Lionsgate",
    network: null,
    production_company: "Pressman Film",
    location: null,
    genre: null,
    budget: null,
    synopsis: "Remake of the Christian Bale film. Wealthy New York investment banker, Patrick Bateman, lives a twisted secret double life. Inspired by the novel by Bret Easton Ellis.",
    source_agency: "IE",
    start_date: "2026-09-01",
    start_date_as_written: "September 2026",
    wrap_date: null,
    wrap_date_as_written: "December 2026",
    key_people: ["Luca Guadagnino", "Sam Pressman", "Jessica Ronane"],
  },
  {
    id: "p2",
    title: "AMERICAN PSYCHO",
    project_type: "Feature Film",
    status_raw: "Active",
    canonical_stage: "development",
    studio: "Lionsgate-Summit",
    network: null,
    production_company: "LuckyChap",
    location: null,
    genre: null,
    budget: null,
    synopsis: "For original movie: A wealthy New York City investment banking executive hides his alternate psychopathic ego from his co-workers and friends as he delves deeper into his violent, hedonistic fantasies.",
    source_agency: "WME",
    start_date: null,
    wrap_date: null,
    key_people: ["Luca Guadagnino", "Scott Z. Burns", "Josey McNamara"],
  },
  {
    id: "p3",
    title: "Bell Jar, The",
    project_type: "Feature Film",
    status_raw: "Features, New",
    canonical_stage: "development",
    studio: "Focus Features",
    network: null,
    production_company: "Plan B Entertainment StudioCanal",
    location: "Toronto, Canada",
    genre: null,
    budget: null,
    synopsis: "Accomplished student and burgeoning writer ESTHER GREENWOOD secures a coveted internship at a high profile fashion magazine in New York, which offers a chance to experience a lavish life in the city while securing a futu",
    source_agency: "IE",
    start_date: null,
    start_date_sort: "2026-06-15",
    start_date_as_written: "Mid-June 2026",
    wrap_date: null,
    wrap_date_as_written: "Mid-August 2026",
    key_people: ["Sarah Polley", "Joy Gorman Dede Gardner", "Jason Knight"],
  },
  {
    id: "p4",
    title: "Better Than The Movies",
    project_type: "Feature Film",
    status_raw: "Features, New",
    canonical_stage: "development",
    studio: "Netflix",
    network: null,
    production_company: "Original Headquarters",
    location: "Chicago, IL",
    genre: null,
    budget: null,
    synopsis: "In this love letter to romantic comedies, senior Liz Buxbaums obsession with rom-com movies proves very helpful when she schemes with her annoying but cute next-door neighbor to get the attention of her untouchable crush",
    source_agency: "IE",
    start_date: null,
    start_date_as_written: "September 2026",
    wrap_date: null,
    wrap_date_as_written: "October 2026",
    key_people: ["Julia Hart", "Jordan Horowitz Shauna Phelan", "Melissa Kostenbauder"],
  },
  {
    id: "p5",
    title: "Boy Moms",
    project_type: "Feature Film",
    status_raw: "Features, New",
    canonical_stage: "development",
    studio: "MRC Studios",
    network: null,
    production_company: "LuckyChap Entertainment",
    location: "NY/NJ",
    genre: null,
    budget: null,
    synopsis: "A British woman visiting her boyfriend's American hometown for Thanksgiving becomes embroiled in a bizarre series of events involving overbearing mothers, a peculiar gender reveal party, and a mysterious curse affecting ",
    source_agency: "IE",
    start_date: "2026-08-01",
    start_date_as_written: "Late Summer/ Early Fall 2026",
    wrap_date: null,
    key_people: ["Amanda Rosenberg", "Brye Adler Jessica Switch", "Allison Jones"],
  },
  {
    id: "p6",
    title: "Catch, The AKA Caught Looking",
    project_type: "Feature Film",
    status_raw: "Features, New",
    canonical_stage: "development",
    studio: "Universal Pictures",
    network: null,
    production_company: "21 Laps Entertainment Fruit Tree",
    location: "New York",
    genre: "Romantic Comedy",
    budget: null,
    synopsis: "Plot details remain unknown other then it being described as a two-hander rom-com.",
    source_agency: "IE",
    start_date: null,
    start_date_as_written: "July 2026",
    wrap_date: null,
    key_people: ["Dave McCary", "Shawn Levy Dan Levine", "Francine Maisler"],
  },
  {
    id: "p7",
    title: "6B1B",
    project_type: "Episodic",
    status_raw: "New, Pilots & Episodics",
    canonical_stage: "development",
    studio: "Universal Television Studios",
    network: "Universal Television Studios",
    production_company: null,
    location: "New York, NY",
    genre: null,
    budget: null,
    synopsis: "A hopeful newcomer moves into a chaotic six-bedroom apartment in New York and tries to turn his dysfunctional roommates into a family before a rent increase tears them apart.",
    source_agency: "IE",
    start_date: "2026-08-04",
    wrap_date: "2026-09-05",
    key_people: ["Colin Jost", "Seth White"],
  },
  {
    id: "p8",
    title: "9/12",
    project_type: "Limited Series",
    status_raw: "New, Pilots & Episodics",
    canonical_stage: "development",
    studio: "Paramount+",
    network: "Paramount+",
    production_company: "Sister",
    location: "New York, NY.",
    genre: null,
    budget: null,
    synopsis: "About ailing 9/11 first responders' David vs. Goliath legal fight for compensation. It dramatizes a landmark case that reshaped the fight for accountability: a relentless legal war that stretched nearly a decade to secur",
    source_agency: "IE",
    start_date: null,
    start_date_sort: "2026-08-25",
    start_date_as_written: "Late August 2026",
    wrap_date: null,
    wrap_date_as_written: "December 2026",
    key_people: ["Tobias Lindholm", "Caroline Blanco Jonathan Filley", "Jennifer Venditti"],
  },
  {
    id: "p9",
    title: "Alan Chikin Chow / Hybe Project",
    project_type: "Episodic",
    status_raw: "New, Pilots & Episodics",
    canonical_stage: "development",
    studio: "Netflix",
    network: "Netflix",
    production_company: null,
    location: "Los Angeles, California",
    genre: null,
    budget: null,
    synopsis: "NETFLIX UNTITLED ACC / HYBE PROJECT is a musical comedy series about Alan, an idol trainee reject who transfers to a global arts academy and recruits a group of misfits to create his own pop group.",
    source_agency: "IE",
    start_date: null,
    start_date_as_written: "July 2026",
    wrap_date: null,
    key_people: ["Kerrie Mailey"],
  },
  {
    id: "p10",
    title: "Beat The Reaper AKA Easter Island",
    project_type: "Episodic",
    status_raw: "New, Pilots & Episodics",
    canonical_stage: "development",
    studio: "New Regency Productions, Apple Studios",
    network: "New Regency Productions, Apple Studios",
    production_company: null,
    location: "New York",
    genre: "Dramedy",
    budget: null,
    synopsis: "Based on the novel of the same name by Josh Bazell, the dark comedy tells the story of medical resident Peter Brown, who is also in the Federal Witness Protection Program due to his past as a mafia hitman. When a dying m",
    source_agency: "IE",
    start_date: null,
    start_date_as_written: "September 2026",
    wrap_date: null,
    wrap_date_as_written: "December 2026",
    key_people: ["Alexa Fogel"],
  },
  {
    id: "p11",
    title: "Black Diamond",
    project_type: "Episodic",
    status_raw: "New, Pilots & Episodics",
    canonical_stage: "development",
    studio: "Netflix",
    network: "Netflix",
    production_company: "Jodi Matterson Jeremy Platt Libbie Doherty Erika North Mel Turner",
    location: "Queenstown, NZ",
    genre: null,
    budget: null,
    synopsis: "When a billionaire ski resort mogul dies, his two New Zealand daughters think they're next in line to take over - until their dad's secret eldest American daughter shows up and claims the empire for herself. As power str",
    source_agency: "IE",
    start_date: "2026-07-13",
    wrap_date: "2026-10-02",
    key_people: ["Glendyn Ivin Roseanne Liang", "Mary Vernieu"],
  },
  {
    id: "p12",
    title: "Buck AKA Untl. Rodeo Project",
    project_type: "Straight to Series",
    status_raw: "New, Pilots & Episodics",
    canonical_stage: "development",
    studio: "Counterpart Studios",
    network: "Counterpart Studios",
    production_company: null,
    location: "ATL",
    genre: null,
    budget: null,
    synopsis: "The eight-part family drama unfolds in Southeast Texas, where rodeo isn't just what you do - it's who you are, per the logline. At the center are three siblings bound by the legacy their mother built and the unfinished b",
    source_agency: "IE",
    start_date: "2026-08-19",
    wrap_date: "2026-11-20",
    key_people: ["TBD", "Lauren Grey"],
  },
  {
    id: "p13",
    title: "DRIVER, THE",
    project_type: "Feature Film",
    status_raw: "Active",
    canonical_stage: "development",
    studio: "Miramax",
    network: null,
    production_company: null,
    location: null,
    genre: null,
    budget: null,
    synopsis: "David Weaver is on his way to work when he finds a stranger tied up in the trunk of his car, along with a bomb and a ringing cell phone. What started out as a simple commute to the office ends up as a fight to save his o",
    source_agency: "WME",
    start_date: "2026-02-01",
    start_date_as_written: "Aiming for Q1 or Q2 2026",
    wrap_date: null,
    key_people: ["Angel Manuel Soto", "Spenser Cohen", "Zach Luna"],
  },
  {
    id: "p14",
    title: "UNT. SOFIA COPPOLA",
    project_type: "Feature Film",
    status_raw: "Active",
    canonical_stage: "development",
    studio: null,
    network: null,
    production_company: null,
    location: null,
    genre: null,
    budget: null,
    synopsis: null,
    source_agency: "WME",
    start_date: null,
    wrap_date: null,
    key_people: ["Sofia Coppola", "Sofia Coppola", "Youree Henley"],
  },
  {
    id: "p15",
    title: "VALET, THE",
    project_type: "Feature Film",
    status_raw: "Active, Actively Casting",
    canonical_stage: "casting",
    studio: "Miramax",
    network: null,
    production_company: null,
    location: "Rome & Northern Italy",
    genre: "Action",
    budget: null,
    synopsis: "When a young valet driver inadvertently crosses paths with a deadly female spy, he finds himself dragged into a top secret government operation to prevent a terrorist arms dealer from escaping custody.",
    source_agency: "WME",
    start_date: null,
    wrap_date: null,
    key_people: ["Darrin Prescott", "Jason Dean Hall", "HALLEHOLLY"],
  },
  {
    id: "p16",
    title: "ALONE AT DAWN fka COMBAT CONTROL",
    project_type: "Feature Film",
    status_raw: "Active, Actively Casting",
    canonical_stage: "casting",
    studio: "Amazon Studios",
    network: null,
    production_company: "Hideaway Entertainment; Imagine Entertainment",
    location: "Budapest",
    genre: null,
    budget: null,
    synopsis: "True story of American soldier John Chapman who gave his life in a harrowing battle in Afghanistan and was posthumously awarded the Medal of Honor in 2002 after his sacrifice was properly recognized.",
    source_agency: "WME",
    start_date: null,
    start_date_as_written: "November 2025 - February 2026",
    wrap_date: null,
    key_people: ["Ron Howard", "MICHAEL RUSSELL GUNN", "Allan Mandelbaum"],
  },
  {
    id: "p17",
    title: "BEHEMOTH!",
    project_type: "Feature Film",
    status_raw: "Active, Actively Casting",
    canonical_stage: "casting",
    studio: "Searchlight Pictures",
    network: null,
    production_company: null,
    location: "Los Angeles",
    genre: null,
    budget: null,
    synopsis: "When a nomadic cellist returns to a historic recording studio for a film score session, he must navigate the complex relationships and personal demons that resurface, all while discovering a profound connection to a youn",
    source_agency: "WME",
    start_date: null,
    start_date_as_written: "Aim for October 2025",
    wrap_date: null,
    key_people: ["Tony Gilroy", "Tony Gilroy", "Gena Konstantinakos"],
  },
  {
    id: "p18",
    title: "CASH BURN",
    project_type: "Feature Film",
    status_raw: "DevLit, Active, Actively Casting",
    canonical_stage: "casting",
    studio: "Netflix",
    network: null,
    production_company: null,
    location: null,
    genre: null,
    budget: null,
    synopsis: "Set in a post 9/11 New York City, the film follows a group of criminal FDNY firefighters who are trying to pull off their biggest job yet in the face of growing suspicions from the police. In the vein of HEAT.",
    source_agency: "WME",
    start_date: null,
    start_date_as_written: "Tent Q2 2026",
    wrap_date: null,
    key_people: ["Justin Kurzel", "Ezra Nachman", "Dan Levine"],
  },
  {
    id: "p19",
    title: "FINAL SCORE, THE",
    project_type: "Feature Film",
    status_raw: "DevLit, Active",
    canonical_stage: "development",
    studio: "Fifth Season (Studio)",
    network: null,
    production_company: "Make Ready",
    location: null,
    genre: null,
    budget: null,
    synopsis: "Two FBI agents are pit against a crew of bank robbers -- and each other -- as they grapple with order and chaos in this propulsive action thriller that explores the real value of the things we keep locked away.",
    source_agency: "WME",
    start_date: null,
    start_date_as_written: "Q3, Q4 2025",
    wrap_date: null,
    key_people: ["NICK ROWLAND", "Will Hettinger", "BRAD WESTON"],
  },
  {
    id: "p20",
    title: "HIGHLANDER",
    project_type: "Feature Film",
    status_raw: "Active, Actively Casting",
    canonical_stage: "casting",
    studio: "Amazon Studios",
    network: null,
    production_company: "Original Film; United Artists",
    location: "UK",
    genre: "Action, Adventure, Fantasy",
    budget: null,
    synopsis: "An immortal Scottish swordsman must confront the last of his immortal opponent, a murderously brutal barbarian who lusts for the fabled “prize.” Remake of the 1986 film.",
    source_agency: "WME",
    start_date: null,
    start_date_as_written: "tent. December 2025",
    wrap_date: null,
    key_people: ["CHAD STAHELSKI", "James Coyne", "SCOTT STUBER"],
  },
] as Omit<
  Project,
  "directors" | "producers" | "start_date_as_written" | "wrap_date_as_written" | "status_tags"
>[]).map((p) => ({
  // Null unless a literal above says otherwise, so the fixtures carry the
  // same shape the API does rather than undefined where it sends null.
  start_date_as_written: null,
  wrap_date_as_written: null,
  ...p,
  status_tags: classify(p.status_raw),
  directors: [],
  producers: [],
}));

/* Contacts, real names and roles out of the IE grids.
 *
 * The numbers and addresses are not real. The grids carry the genuine ones,
 * including personal mobiles, and this build is deployed publicly, so
 * shipping them would put working casting directors' direct lines in front
 * of anyone with the link. The screen is demonstrating that the contacts are
 * already gathered and dialable, which a formatted placeholder shows just as
 * well as a real number. */
const contactsByProject: Record<string, Contact[]> = {
  "p1": [
      {
        name: "Jessica Ronane",
        role: "Casting Director",
        phone: "+1 (310) 555-1037",
        email: "jessica.ronane@castingoffice.example",
      },
      {
        name: "Luca Guadagnino",
        role: "Director",
        phone: "+1 (310) 555-1074",
        email: "luca.guadagnino@castingoffice.example",
      },
      {
        name: "Sam Pressman",
        role: "Producer",
        phone: "+1 (310) 555-1111",
        email: "sam.pressman@castingoffice.example",
      },
  ],
  "p3": [
      {
        name: "Jason Knight",
        role: "Casting Director",
        phone: "+1 (310) 555-1148",
        email: "jason.knight@castingoffice.example",
      },
      {
        name: "Sarah Polley",
        role: "Director",
        phone: "+1 (310) 555-1185",
        email: "sarah.polley@castingoffice.example",
      },
      {
        name: "Joy Gorman Dede Gardner",
        role: "Producer",
        phone: "+1 (310) 555-1222",
        email: "joy.gorman.dede.gardner@castingoffice.example",
      },
  ],
  "p4": [
      {
        name: "Melissa Kostenbauder",
        role: "Casting Director",
        phone: "+1 (310) 555-1259",
        email: "melissa.kostenbauder@castingoffice.example",
      },
      {
        name: "Julia Hart",
        role: "Director",
        phone: "+1 (310) 555-1296",
        email: "julia.hart@castingoffice.example",
      },
      {
        name: "Jordan Horowitz Shauna Phelan",
        role: "Producer",
        phone: "+1 (310) 555-1333",
        email: "jordan.horowitz.shauna.phelan@castingoffice.example",
      },
  ],
  "p5": [
      {
        name: "Allison Jones",
        role: "Casting Director",
        phone: "+1 (310) 555-1370",
        email: "allison.jones@castingoffice.example",
      },
      {
        name: "Amanda Rosenberg",
        role: "Director",
        phone: "+1 (310) 555-1407",
        email: "amanda.rosenberg@castingoffice.example",
      },
      {
        name: "Brye Adler Jessica Switch",
        role: "Producer",
        phone: "+1 (310) 555-1444",
        email: "brye.adler.jessica.switch@castingoffice.example",
      },
  ],
  "p6": [
      {
        name: "Francine Maisler",
        role: "Casting Director",
        phone: "+1 (310) 555-1481",
        email: "francine.maisler@castingoffice.example",
      },
      {
        name: "Dave McCary",
        role: "Director",
        phone: "+1 (310) 555-1518",
        email: "dave.mccary@castingoffice.example",
      },
      {
        name: "Shawn Levy Dan Levine",
        role: "Producer",
        phone: "+1 (310) 555-1555",
        email: "shawn.levy.dan.levine@castingoffice.example",
      },
  ],
  "p7": [
      {
        name: "Seth White",
        role: "Casting Director",
        phone: "+1 (310) 555-1592",
        email: "seth.white@castingoffice.example",
      },
      {
        name: "Colin Jost",
        role: "Director",
        phone: "+1 (310) 555-1629",
        email: "colin.jost@castingoffice.example",
      },
  ],
  "p8": [
      {
        name: "Jennifer Venditti",
        role: "Casting Director",
        phone: "+1 (310) 555-1666",
        email: "jennifer.venditti@castingoffice.example",
      },
      {
        name: "Tobias Lindholm",
        role: "Director",
        phone: "+1 (310) 555-1703",
        email: "tobias.lindholm@castingoffice.example",
      },
      {
        name: "Caroline Blanco Jonathan Filley",
        role: "Producer",
        phone: "+1 (310) 555-1740",
        email: "caroline.blanco.jonathan.filley@castingoffice.example",
      },
  ],
  "p9": [
      {
        name: "Kerrie Mailey",
        role: "Casting Director",
        phone: "+1 (310) 555-1777",
        email: "kerrie.mailey@castingoffice.example",
      },
  ],
  "p10": [
      {
        name: "Alexa Fogel",
        role: "Casting Director",
        phone: "+1 (310) 555-1814",
        email: "alexa.fogel@castingoffice.example",
      },
  ],
  "p11": [
      {
        name: "Mary Vernieu",
        role: "Casting Director",
        phone: "+1 (310) 555-1851",
        email: "mary.vernieu@castingoffice.example",
      },
      {
        name: "Glendyn Ivin Roseanne Liang",
        role: "Director",
        phone: "+1 (310) 555-1888",
        email: "glendyn.ivin.roseanne.liang@castingoffice.example",
      },
  ],
  "p12": [
      {
        name: "Lauren Grey",
        role: "Casting Director",
        phone: "+1 (310) 555-1925",
        email: "lauren.grey@castingoffice.example",
      },
  ],
};

/* Director and producer come from the labelled contacts above and from
 * nowhere else.
 *
 * key_people is tempting because it covers every production and its order
 * looks like director, producers, casting director. It holds on all eleven
 * records that carry labels. It is still not safe to read: on a production
 * with no director in the grid the first name is whoever came first, which
 * for p9 and p10 is a casting director, Alexa Fogel and Mary Vernieu, and for
 * p12 is the string TBD. Reading by position would print a casting director
 * in the Director column and look exactly as confident as a right answer.
 *
 * So nine productions show nothing in these columns, which is the truthful
 * answer to what the stub data knows. It is a property of the fixtures rather
 * than of the grids: the source has Director on 76 of 77 IE film records and
 * Producer on 68, and the endpoint now returns both, so the columns fill up
 * when the screens come off fixtures. */
for (const p of projects) {
  p.contacts = contactsByProject[p.id] ?? [];
  const named = (capacity: string): string[] =>
    (p.contacts ?? []).filter((c) => c.role === capacity).map((c) => c.name);
  p.directors = named("Director");
  p.producers = named("Producer");
}

/* Logged calls and emails. No table exists for these yet, so anything added
 * during a session lives in memory and is gone on reload. */
const calls: CallLogEntry[] = [
  {
    id: "cl1",
    project_id: "p1",
    occurred_at: daysAgo(1),
    contact: "Jessica Ronane (Casting Director)",
    kind: "Phone",
    notes: "Discussed availability and interest, she is very enthusiastic.",
    follow_up: "Chemistry read next week",
  },
  {
    id: "cl2",
    project_id: "p1",
    occurred_at: daysAgo(3),
    contact: "Sam Pressman (Producer)",
    kind: "Email",
    notes: "Sent updated reel and headshots.",
    follow_up: "Awaiting response",
  },
  {
    id: "cl3",
    project_id: "p1",
    occurred_at: daysAgo(6),
    contact: "Jessica Ronane (Casting Director)",
    kind: "Phone",
    notes: "Initial enquiry about the Patrick Bateman role.",
    follow_up: null,
  },
];

/** The fixtures, without the read state, which is not a property of the
 *  fixture but of who is looking at it. */
type Fixture = Omit<RoleWithProject, "seen" | "role_type_key">;

const fixtures: Fixture[] = [
  {
    id: "r1",
    project_id: "p1",
    name: "Patrick Bateman",
    role_type: "Lead",
    description: "Fit and handsome; he starts the story as an 17-year-old from Ohio. Ambitious, fearless, but detached. The script follows him through military service, where his intelligence and precision turn violent. By the time he reaches London, he's confident, well-dresse",
    gender: "Male",
    age_min: 25,
    age_max: 32,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[0],
    candidate_count: 3,
    actors_mentioned: 3,
    top_standing: "attached",
    submission_state: "audition",
    matched_at: daysAgo(1),
  },
  {
    id: "r2",
    project_id: "p1",
    name: "Det. Donald Kimball",
    role_type: "Supporting Lead",
    description: "He is always dressed in an ill-fitting suit and posses the look of a civil servant - a bit awkward, he always tries hard to hide it. He is a private detective that has been hired to investigate the disappearance of Paul Owens. Patrick shoots and kills him towa",
    gender: "Male",
    age_min: 30,
    age_max: 39,
    ethnicities: [],
    is_open: true,
    project: projects[0],
    candidate_count: 0,
    actors_mentioned: 2,
    top_standing: "offer",
    submission_state: null,
    matched_at: daysAgo(2),
  },
  {
    id: "r3",
    project_id: "p1",
    name: "Timothy Price",
    role_type: "Supporting Lead",
    description: "Described as good looking, Price is styled and groomed within an inch of his life. He is one of the best and brightest in the world of banking. Price is a good friend of Patrick and he is completely oblivious to Patrick's murderous ways. Price frequently engag",
    gender: "Male",
    age_min: 27,
    age_max: 27,
    ethnicities: [],
    is_open: true,
    project: projects[0],
    candidate_count: 0,
    actors_mentioned: 1,
    top_standing: "passed",
    submission_state: "callback",
    matched_at: daysAgo(3),
  },
  {
    id: "r4",
    project_id: "p1",
    name: "Evelyn",
    role_type: "Supporting",
    description: "Blonde, beautiful, and cheerful. Evelyn is a Stepford wife in the making. She is engaged to Patrick who is miserable being with her. She is completely oblivious to Patrick's affairs and his murderous ways. Patrick contemplates killing her, but he instead just ",
    gender: "Female",
    age_min: 25,
    age_max: 25,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[0],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(4),
  },
  {
    id: "r5",
    project_id: "p2",
    name: "Patrick Bateman",
    role_type: "Leading",
    description: "Top Choice: AUSTIN BUTLER",
    gender: "Male",
    age_min: null,
    age_max: null,
    ethnicities: [],
    is_open: true,
    project: projects[1],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(5),
  },
  {
    id: "r6",
    project_id: "p3",
    name: "Esther Greenwood",
    role_type: "Lead",
    description: "A talented writer who wins a guest editorship at Ladies' Day Magazine in New York City. Despite her achievements, Esther struggles with mental health issues, feeling disconnected and overwhelmed by societal expectations and her own ambitions.",
    gender: "Female",
    age_min: 19,
    age_max: 19,
    ethnicities: [],
    is_open: true,
    project: projects[2],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(6),
  },
  {
    id: "r7",
    project_id: "p3",
    name: "Mrs. Greenwood",
    role_type: "Supporting Lead",
    description: "Esther's practical and traditional mother who struggles to understand Esther's mental health issues.",
    gender: "Female",
    age_min: 40,
    age_max: 49,
    ethnicities: [],
    is_open: true,
    project: projects[2],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(7),
  },
  {
    id: "r8",
    project_id: "p3",
    name: "Jay Cee",
    role_type: "Supporting",
    description: "A confident and intelligent editor at Ladies' Day Magazine who mentors Esther. Jay Cee is admired by Esther for her professionalism and intelligence, providing guidance and encouragement, though Esther struggles to meet her expectations.",
    gender: "Female",
    age_min: 35,
    age_max: 37,
    ethnicities: [],
    is_open: true,
    project: projects[2],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(8),
  },
  {
    id: "r9",
    project_id: "p3",
    name: "Doreen",
    role_type: "Supporting",
    description: "20. Confident and gorgeous, Doreen is one of the girls who joins Esther for the Ladies' Day summer internship, and becomes her best friend during her time in New York. She is adventurous and brassy. A \"broad\" in the old fashioned sense...",
    gender: "Female",
    age_min: 20,
    age_max: 20,
    ethnicities: [],
    is_open: true,
    project: projects[2],
    candidate_count: 2,
    submission_state: null,
    matched_at: daysAgo(9),
  },
  {
    id: "r10",
    project_id: "p4",
    name: "Liz",
    role_type: "Lead",
    description: "An 18-25 year old (to play 17) white or mixed-race young woman who is a redheaded rom-com obsessed dreamer that views life through the lens of her favorite movies. Smart, funny, and hopelessly romantic. Beneath her quirky optimism is a young woman still naviga",
    gender: "Female",
    age_min: 18,
    age_max: 25,
    ethnicities: ["White", "mixed"],
    is_open: true,
    project: projects[3],
    candidate_count: 3,
    submission_state: "submitted",
    matched_at: daysAgo(10),
  },
  {
    id: "r11",
    project_id: "p4",
    name: "Wes",
    role_type: "Lead",
    description: "An 18-25 year old (to play 17) young man. Open race/ethnicity. He is a local baseball star, and Liz's next-door neighbor and longtime nemesis. Athletic with dark features. Sarcastic, charismatic, and effortlessly cool, with a sharp wit and a tendency to hide h",
    gender: "Male",
    age_min: 18,
    age_max: 25,
    ethnicities: [],
    is_open: true,
    project: projects[3],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(11),
  },
  {
    id: "r12",
    project_id: "p4",
    name: "Michael",
    role_type: "Lead",
    description: "An 18-25 year old (to play 17) young man. Open race/ethnicity. He's attractive, charming, and seemingly straight out of a teen romance. Kind and likable, he's Liz's longtime crush who returns to town during senior year, reigniting her belief in a perfect movie",
    gender: "Male",
    age_min: 18,
    age_max: 25,
    ethnicities: [],
    is_open: true,
    project: projects[3],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(0),
  },
  {
    id: "r13",
    project_id: "p5",
    name: "Fran",
    role_type: "Lead",
    description: "Late 20s. Dry, self-deprecating, and desperately people-pleasing in equal measure; she's the emotional spine of the story, navigating grief, displacement, and a battlefield she never signed up for with more grace than anyone deserves to.",
    gender: "Female",
    age_min: 27,
    age_max: 29,
    ethnicities: [],
    is_open: true,
    project: projects[4],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(1),
  },
  {
    id: "r14",
    project_id: "p5",
    name: "Deb",
    role_type: "Lead",
    description: "60s, Julian's mother. Open A stone-cold fox who runs her household like a five-star hotel and her son's love life like a hostile acquisition, elegant, withering, and operating with a motive no one can quite pin down until it's too late.",
    gender: "Female",
    age_min: 60,
    age_max: 69,
    ethnicities: [],
    is_open: true,
    project: projects[4],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(2),
  },
  {
    id: "r15",
    project_id: "p5",
    name: "Julian",
    role_type: "Lead",
    description: "Male, 30s, Open. preppy, classically handsome in a way that's almost its own personality flaw. Charming enough to keep Fran hooked and spineless enough to prove every red flag right; he loves Fran, but when it counts, he goes back inside.",
    gender: "Male",
    age_min: 30,
    age_max: 39,
    ethnicities: [],
    is_open: true,
    project: projects[4],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(3),
  },
  {
    id: "r16",
    project_id: "p5",
    name: "Harold",
    role_type: "Supporting",
    description: "70s, Julian's father. Communicates exclusively in grunts and grievances; present in body, absent in every other sense. The scissors are never where he left",
    gender: "Male",
    age_min: 70,
    age_max: 79,
    ethnicities: [],
    is_open: true,
    project: projects[4],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(4),
  },
  {
    id: "r17",
    project_id: "p6",
    name: "Aunt Fiona",
    role_type: "Supporting",
    description: null,
    gender: "Female",
    age_min: null,
    age_max: null,
    ethnicities: [],
    is_open: true,
    project: projects[5],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(5),
  },
  {
    id: "r18",
    project_id: "p6",
    name: "Bill",
    role_type: "Supporting",
    description: null,
    gender: "Male",
    age_min: null,
    age_max: null,
    ethnicities: [],
    is_open: true,
    project: projects[5],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(6),
  },
  {
    id: "r19",
    project_id: "p6",
    name: "Soupy",
    role_type: "Supporting",
    description: "Chris Pine's security guard",
    gender: "Male",
    age_min: null,
    age_max: null,
    ethnicities: [],
    is_open: true,
    project: projects[5],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(7),
  },
  {
    id: "r20",
    project_id: "p6",
    name: "Jessie",
    role_type: "Supporting",
    description: null,
    gender: "Female",
    age_min: null,
    age_max: null,
    ethnicities: [],
    is_open: true,
    project: projects[5],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(8),
  },
  {
    id: "r21",
    project_id: "p7",
    name: "Andrew",
    role_type: "Series Regular",
    description: "Character is a male in his mid 20s. A college football player from a small town whose pro sports dreams didn't pan out, he turns lemons into lemonade by moving to New York to figure out his next chapter. Down to embrace everything the city has to offer, he is ",
    gender: "Male",
    age_min: 25,
    age_max: 27,
    ethnicities: [],
    is_open: true,
    project: projects[6],
    candidate_count: 1,
    submission_state: "audition",
    matched_at: daysAgo(9),
  },
  {
    id: "r22",
    project_id: "p7",
    name: "Sam",
    role_type: "Series Regular",
    description: "Character is a female in her late 20s-early 30s. She is tough, jaded, and fiercely independent. The unofficial leader of the apartment who keeps everyone at arm's length. She works at a grocery store but thinks she's destined for more. From the outside, she is",
    gender: "Female",
    age_min: 27,
    age_max: 33,
    ethnicities: [],
    is_open: true,
    project: projects[6],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(10),
  },
  {
    id: "r23",
    project_id: "p7",
    name: "Lauren",
    role_type: "Series Regular",
    description: "Character is a female in her late 20s. She is bubbly, upbeat, and deceptively calculating. She works for Teach For America and she sees herself as an inspirational force, despite being deeply self-absorbed and chaotic. She is constantly trying to maintain the ",
    gender: "Female",
    age_min: 27,
    age_max: 29,
    ethnicities: [],
    is_open: true,
    project: projects[6],
    candidate_count: 1,
    submission_state: "callback",
    matched_at: daysAgo(11),
  },
  {
    id: "r24",
    project_id: "p7",
    name: "Gary",
    role_type: "Series Regular",
    description: "Character is a male in his late 20s. He and Huxley are friends from college and he helped Huxley get a room in the apartment. He is charming, sarcastic, and emotionally evasive. He is a well-dressed bartender with an easy confidence, but inside he is actually ",
    gender: "Male",
    age_min: 27,
    age_max: 29,
    ethnicities: [],
    is_open: true,
    project: projects[6],
    candidate_count: 1,
    submission_state: null,
    matched_at: daysAgo(0),
  },
  {
    id: "r25",
    project_id: "p8",
    name: "Jason Smith",
    role_type: "Lead",
    description: "A composite fictional character inspired by real-life people - who risked everything to take on entrenched institutions, exposing the politics, greed, corruption and betrayal that compounded tragedy set against the emergency workers' remarkable unity and brave",
    gender: "Male",
    age_min: 40,
    age_max: 49,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[7],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(1),
  },
  {
    id: "r26",
    project_id: "p8",
    name: "Coach",
    role_type: "Series Regular",
    description: "Male. White. Late 40s-early 50s. Working class Long Island. 9/11 first responder, former police officer, and youth hockey coach. He is suffering from leukemia, believed to be a result of his work at Ground Zero. Initially proud and reluctant to sue his city, h",
    gender: "Male",
    age_min: 37,
    age_max: 43,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[7],
    candidate_count: 3,
    submission_state: "audition",
    matched_at: daysAgo(2),
  },
  {
    id: "r27",
    project_id: "p8",
    name: "Molly",
    role_type: "Series Regular",
    description: "Female. White. Late 30s / Early 40s. Working class Long Island. Coach's wife. She is deeply concerned for her husband's health and their family's future, especially with a newborn daughter, Ingrid. She is the one who initially seeks legal help and supports Jas",
    gender: "Female",
    age_min: 37,
    age_max: 43,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[7],
    candidate_count: 3,
    submission_state: null,
    matched_at: daysAgo(3),
  },
  {
    id: "r28",
    project_id: "p8",
    name: "The Lost Son",
    role_type: "Recurring",
    description: "Male. Any ethnicity. 16 to 21. A homeless young man and an enigmatic figure through the series who has experienced hardship at a young age. First seen sleeping in front of a fire station. He is offered kindness by the House Mouse, representing the forgotten an",
    gender: "Male",
    age_min: 16,
    age_max: 21,
    ethnicities: [],
    is_open: true,
    project: projects[7],
    candidate_count: 6,
    submission_state: "callback",
    matched_at: daysAgo(4),
  },
  {
    id: "r29",
    project_id: "p9",
    name: "Princeton",
    role_type: "Series Regular",
    description: "18 year old (submit 18-24) Korean male. Gorgeous and mysterious, like a teenage K-drama leading man. In our love triangle, he's the Edward Cullen bad boy contrast to Alan's puppy-dog-like loving energy. Princeton is the black sheep of the group, carrying a dar",
    gender: "Male",
    age_min: 18,
    age_max: 24,
    ethnicities: ["Korean"],
    is_open: true,
    project: projects[8],
    candidate_count: 1,
    submission_state: null,
    matched_at: daysAgo(5),
  },
  {
    id: "r30",
    project_id: "p9",
    name: "Aura",
    role_type: "Series Regular",
    description: "Woman; 18 to 24 years old; East Asian.(Character is a Korean teenage girl. Performer must be 18+ to play younger.) Our female all-rounder. Smart, passionate, and perpetually distracted by cute boys. She's a girl boss who's constantly at war with her boy-crazy ",
    gender: "Female",
    age_min: 18,
    age_max: 24,
    ethnicities: ["East Asian"],
    is_open: true,
    project: projects[8],
    candidate_count: 1,
    submission_state: "submitted",
    matched_at: daysAgo(6),
  },
  {
    id: "r31",
    project_id: "p9",
    name: "Ruby",
    role_type: "Series Regular",
    description: "Woman; 18 to 24 years old; any ethnicity.(Character is a teenage girl. Performer must be 18+ to play younger) Our female dancer. Tough, sarcastic, and filled with a bitterness beyond her years. Ruby was formerly part of the Ballet Baddies, Idol Academy's most ",
    gender: "Female",
    age_min: 18,
    age_max: 24,
    ethnicities: [],
    is_open: true,
    project: projects[8],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(7),
  },
  {
    id: "r32",
    project_id: "p9",
    name: "Bellaire",
    role_type: "Series Regular",
    description: "Woman; 18 to 24 years old; any ethnicity. (Character is a teenage girl. Performer must be 18+ to play younger) Our female vocalist. She's a theater diva who is absolutely insufferable, but when she opens her mouth, she can SANG tha house down. Her relationship",
    gender: "Female",
    age_min: 18,
    age_max: 24,
    ethnicities: [],
    is_open: true,
    project: projects[8],
    candidate_count: 1,
    submission_state: null,
    matched_at: daysAgo(8),
  },
  {
    id: "r33",
    project_id: "p10",
    name: "Dr. Peter Brown",
    role_type: "Series Regular",
    description: "An intern at Boston's worst hospital, with a talent for medicine, a shift from hell, and a past he'd prefer to keep hidden. But when a patient recognizes him from his dangerous past, Brown has eight hours to elude the government, mob hitmen, quack surgeons, an",
    gender: "Male",
    age_min: 25,
    age_max: 35,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[9],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(9),
  },
  {
    id: "r34",
    project_id: "p11",
    name: "Daniel Everett",
    role_type: "Recurring",
    description: "Late 50s to 60s white man. The patriarch of the Everett family. He's a charming, rogue-ish, irascible billionaire. As a former professional skier who married very well, he used his wife's family money to parlay his way into building a multi-billion-dollar busi",
    gender: "Male",
    age_min: 57,
    age_max: 69,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[10],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(10),
  },
  {
    id: "r35",
    project_id: "p11",
    name: "Clara Downing",
    role_type: "Series Regular",
    description: "30s, white or mixed ethnicity woman. Clara is gorgeous, put together, and driven. A girl who came from nothing and was raised by her loving single mother. When we meet her, Clara is the superstar manager of the Everett Aspen resort and we think she is having a",
    gender: "Female",
    age_min: 30,
    age_max: 39,
    ethnicities: ["Caucasian", "mixed"],
    is_open: true,
    project: projects[10],
    candidate_count: 3,
    submission_state: "submitted",
    matched_at: daysAgo(11),
  },
  {
    id: "r36",
    project_id: "p12",
    name: "Elias Clarke",
    role_type: "Series Regular",
    description: "Character portrayed is male, Black, in his 60s. The Foundation. The Misunderstood Father. Easy-going, persuasive, and quietly terrifying. But still a Zaddy. Co- founder of the family rodeo, Elias is a disgraced outlaw and father to the Clarke siblings. He cloc",
    gender: "Male",
    age_min: 60,
    age_max: 69,
    ethnicities: ["Black"],
    is_open: true,
    project: projects[11],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(0),
  },
  {
    id: "r37",
    project_id: "p12",
    name: "Sebastian Clarke",
    role_type: "Series Regular",
    description: "Character portrayed is Male, Black, late 30s. The Not-So Golden Boy. Fearless. Reckless. Manipulative. A relatable Everyman. Still... athletic and '90s fine. He's a recently divorced, former bull-riding champion, whose career was cut short after a horrific acc",
    gender: "Male",
    age_min: 37,
    age_max: 39,
    ethnicities: ["Black"],
    is_open: true,
    project: projects[11],
    candidate_count: 1,
    submission_state: null,
    matched_at: daysAgo(1),
  },
  {
    id: "r38",
    project_id: "p12",
    name: "Rodney Clarke",
    role_type: "Series Regular",
    description: "Character portrayed is male, Black, late 20s. The Fire-starter. Brash. Hot Boy. F**k Boy. Sebastian and Anaya's smart-ass, gay younger brother. The sibling most likely to burn everything down. He loves strip clubs, blunts, and money. Rodney has zero filter, of",
    gender: "Male",
    age_min: 27,
    age_max: 29,
    ethnicities: ["Black"],
    is_open: true,
    project: projects[11],
    candidate_count: 1,
    submission_state: "callback",
    matched_at: daysAgo(2),
  },
  {
    id: "r39",
    project_id: "p12",
    name: "Anaya Clarke-Reagan",
    role_type: "Series Regular",
    description: "Character portrayed is female, Black, early 40s. The Firstborn. Uncompromising. Savvy. Polished. She looks expensive. The highly-educated eldest sibling and the city's youngest former rodeo queen. Trinity City's mayor and a practicing real estate lawyer, who's",
    gender: "Female",
    age_min: 40,
    age_max: 43,
    ethnicities: ["Black"],
    is_open: true,
    project: projects[11],
    candidate_count: 1,
    submission_state: null,
    matched_at: daysAgo(3),
  },
  {
    id: "r40",
    project_id: "p13",
    name: "David Weaver",
    role_type: "Leading",
    description: "David becomes an unwitting participant in a criminal operation, struggling to protect his family while navigating a dangerous situation.",
    gender: "Male",
    age_min: 37,
    age_max: 37,
    ethnicities: [],
    is_open: true,
    project: projects[12],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(4),
  },
  {
    id: "r41",
    project_id: "p13",
    name: "Woman",
    role_type: "Supporting",
    description: "Hired to oversee the execution of the crime, she communicates directly to David and the criminals.",
    gender: "Female",
    age_min: null,
    age_max: null,
    ethnicities: [],
    is_open: true,
    project: projects[12],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(5),
  },
  {
    id: "r42",
    project_id: "p13",
    name: "Suit",
    role_type: "Supporting",
    description: "A well-dressed criminal involved in the bank heist, who becomes a key figure in the unfolding events and ultimately meets his demise.",
    gender: "Male",
    age_min: 42,
    age_max: 42,
    ethnicities: [],
    is_open: true,
    project: projects[12],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(6),
  },
  {
    id: "r43",
    project_id: "p13",
    name: "Heinrich Volker",
    role_type: "Supporting",
    description: "Initially an unconscious man found in David's trunk, later revealed to be the mastermind behind the criminal operation.",
    gender: "Male",
    age_min: null,
    age_max: null,
    ethnicities: ["European"],
    is_open: true,
    project: projects[12],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(7),
  },
  {
    id: "r44",
    project_id: "p14",
    name: "Nancy Spungen",
    role_type: "Leading",
    description: "Nancy was the American girlfriend of English musician Sid Vicious and a figure of the 1970s punk rock scene. Born and raised in Philadelphia, Spungen was an emotionally disturbed child who was diagnosed with schizophrenia at age 15. After being expelled from c",
    gender: "Female",
    age_min: 15,
    age_max: 25,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[13],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(8),
  },
  {
    id: "r45",
    project_id: "p15",
    name: "Ian",
    role_type: "Leading",
    description: "A messily handsome dreamer, Ryan has an undying love for fast cars. Racing them, building them, and even just admiring them from afar. His work as a minimum-wage valet with his friend Teddy allows him to gain access to some of the fastest and most expensive ca",
    gender: "Male",
    age_min: 20,
    age_max: 25,
    ethnicities: [],
    is_open: true,
    project: projects[14],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(9),
  },
  {
    id: "r46",
    project_id: "p15",
    name: "Mia Krane",
    role_type: "Leading",
    description: "Attached: HALLE BERRY A force of nature in a leather jacket, she is cutthroat and a calculated DEA agent. When a mole is suspected within the DEA, she is suspect number 1 since she recently faked her own death. Always alert and perceptive, she constantly is us",
    gender: "Female",
    age_min: null,
    age_max: null,
    ethnicities: [],
    is_open: true,
    project: projects[14],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(10),
  },
  {
    id: "r47",
    project_id: "p15",
    name: "Teddy",
    role_type: "Supporting",
    description: "Ryan’s scrappy and reliable friend, he has big dreams for his and Ryan’s potential custom car garage. He is highly perceptive of people and his surroundings, which plays a role in his habit of stealing from the rich customers that use him as a valet. Outside o",
    gender: "Male",
    age_min: 20,
    age_max: 25,
    ethnicities: [],
    is_open: true,
    project: projects[14],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(11),
  },
  {
    id: "r48",
    project_id: "p15",
    name: "Martha Kenna",
    role_type: "Supporting",
    description: "An ambitious hardass of a woman, she is the Special Agent in Charge within the DEA. She initially pes Mia as the mole due to her faking her own death, but soon with the help of her right-hand agent, Agent Ambers, she learns the mole is actually her superior, L",
    gender: "Female",
    age_min: 50,
    age_max: 60,
    ethnicities: [],
    is_open: true,
    project: projects[14],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(0),
  },
  {
    id: "r49",
    project_id: "p16",
    name: "Cora Alexander",
    role_type: "Leading",
    description: "Set: Anne Hathaway Cora was a former soldier who was charged due to a botched mission and then asked by the Air Force to investigate Chapman’s death exposing her heroics. Technically a fictional character since it's based on the amalgamation of three women inv",
    gender: "Female",
    age_min: 30,
    age_max: 30,
    ethnicities: [],
    is_open: true,
    project: projects[15],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(1),
  },
  {
    id: "r50",
    project_id: "p16",
    name: "John Chapman",
    role_type: "Leading",
    description: "Attached: ADAM DRIVER John was a Combat control Technician (CCT) in the Air Force who was posthumously awarded the military’s highest award, the Medal of Honor, on August 22, 2018 for his actions in the Battle of Takur Ghar during the War in Afghanistan. Chapm",
    gender: "Male",
    age_min: null,
    age_max: null,
    ethnicities: [],
    is_open: true,
    project: projects[15],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(2),
  },
  {
    id: "r51",
    project_id: "p16",
    name: "Valerie 'Val' Chapman",
    role_type: "Supporting",
    description: "Interest: Mackenzie Davis, Britt Lower, GILLIAN JACOBS John's wife; Appears in both storylines (with John at 30 years old and with Cora in her mid 40s); natural, pretty and pragmatic Pennsylvania coal-miner's daughter",
    gender: "Female",
    age_min: 40,
    age_max: 45,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[15],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(3),
  },
  {
    id: "r52",
    project_id: "p16",
    name: "Chief Britt Kelly 'Slab' Slabinski",
    role_type: "Supporting",
    description: "Team leader during Operation Anaconda. Slab is a true believer in \"no man left behind.” This role was previously ‘Slab’ who was a real person and now fictionalizing, so any ethnicity. Should have a degree of physicality to him and capable enough to handle hims",
    gender: "Male",
    age_min: 30,
    age_max: 40,
    ethnicities: [],
    is_open: true,
    project: projects[15],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(4),
  },
  {
    id: "r53",
    project_id: "p17",
    name: "Alex Serian",
    role_type: "Leading",
    description: "Set: Pedro Pascal Alex is a gifted cellist who returns to LA after many years away, prompted by his mother Ginnie’s declining health. Comes from a family of musicians, his life is steeped in music but marred by complex family dynamics, including a strained rel",
    gender: "Male",
    age_min: null,
    age_max: null,
    ethnicities: [],
    is_open: true,
    project: projects[16],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(5),
  },
  {
    id: "r54",
    project_id: "p17",
    name: "Viviana Bren",
    role_type: "Supporting",
    description: "Viviana is a musical prodigy and the daughter of the late Nadia Bren, a celebrated violinist. By fifteen, Viviana has emerged a confident and charismatic musician, leading her own band and captivating audiences with her authentic, ambitious music. When Alex le",
    gender: "Female",
    age_min: 15,
    age_max: 15,
    ethnicities: [],
    is_open: true,
    project: projects[16],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(6),
  },
  {
    id: "r55",
    project_id: "p17",
    name: "Ginnie Serian",
    role_type: "Supporting",
    description: "Ginnie is Alex and Andy’s mother and a former, celebrity harpist who played in grand stages like Vegas. She now suffers from dementia, and her health is declining further, which results in Alex coming back home to LA.",
    gender: "Female",
    age_min: 75,
    age_max: 75,
    ethnicities: [],
    is_open: true,
    project: projects[16],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(7),
  },
  {
    id: "r56",
    project_id: "p17",
    name: "Marika",
    role_type: "Supporting",
    description: "Interest: CHRISTINE KO Marika is a talented musician who was roommates with Nadia at Juilliard who now teaches music in London. At Nadia’s funeral, she runs into Alex (they used to date in college), and have a one night stand.",
    gender: "Female",
    age_min: 40,
    age_max: 40,
    ethnicities: [],
    is_open: true,
    project: projects[16],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(8),
  },
  {
    id: "r57",
    project_id: "p18",
    name: "Patrick O'Neal",
    role_type: "Leading",
    description: "Captain of FDNY Ladder 2, orchestrates bank heists disguised as fire responses, struggles with cancer and family issues, ultimately sacrifices himself to save others.",
    gender: "Male",
    age_min: 45,
    age_max: 60,
    ethnicities: [],
    is_open: true,
    project: projects[17],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(9),
  },
  {
    id: "r58",
    project_id: "p18",
    name: "Phil DeLuca",
    role_type: "Leading",
    description: "Detective investigating the bank heists, brother-in-law to O'Neal, faces moral dilemmas and ultimately survives due to O'Neal's sacrifice.",
    gender: "Male",
    age_min: 30,
    age_max: 40,
    ethnicities: [],
    is_open: true,
    project: projects[17],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(10),
  },
  {
    id: "r59",
    project_id: "p18",
    name: "Connor 'Baby Boy' Byrne",
    role_type: "Supporting",
    description: "Veteran firefighter and member of O'Neal's team, motivated by personal greed, participates in the heists, survives the events.",
    gender: "Male",
    age_min: 30,
    age_max: 40,
    ethnicities: [],
    is_open: true,
    project: projects[17],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(11),
  },
  {
    id: "r60",
    project_id: "p18",
    name: "Seamus 'Hoops' McCann",
    role_type: "Supporting",
    description: "Veteran firefighter, part of O'Neal's team, conflicted about the heists, survives the events.",
    gender: "Male",
    age_min: 30,
    age_max: 40,
    ethnicities: [],
    is_open: true,
    project: projects[17],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(0),
  },
  {
    id: "r61",
    project_id: "p19",
    name: "Gamen",
    role_type: "Leading",
    description: "Interest: Josh Brolin, MATTHEW MCCONAUGHEY, HUGH JACKMAN, JAVIER BARDEM Leader of the FBI’s tactical operations team. His eyes are like two-way mirrors; they offer nothing. He is smart, has a perfect skincare routine, and is the leader of the pack. Seems to be",
    gender: "Male",
    age_min: 50,
    age_max: 60,
    ethnicities: [],
    is_open: true,
    project: projects[18],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(1),
  },
  {
    id: "r62",
    project_id: "p19",
    name: "Andy Walsh",
    role_type: "Leading",
    description: "Top Choice: Taron Edgerton Ex-Special Forces soldier who is recruited by Gamen. has no friends in the office, but is a hard worker. He is someone that you might not notice, but that is Andy's objective. He was an ex special ops coordinator overseas and is home",
    gender: "Male",
    age_min: 35,
    age_max: 40,
    ethnicities: [],
    is_open: true,
    project: projects[18],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(2),
  },
  {
    id: "r63",
    project_id: "p19",
    name: "Katie Martin",
    role_type: "Supporting",
    description: "She is the Senior agent in charge of the LA division of the FBI and oversees Gamen and Andy. She doesn't trust Gamen at all, as she thinks that he acts on his own selfishness instead of the needs of the bureau. Katie is keen on taking down each operation on he",
    gender: "Female",
    age_min: 30,
    age_max: 40,
    ethnicities: [],
    is_open: true,
    project: projects[18],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(3),
  },
  {
    id: "r64",
    project_id: "p19",
    name: "Liz Walsh",
    role_type: "Supporting",
    description: "She is the loving wife of Andy and works as a teacher. She has two priorities in life: her loving husband and her students. She works tirelessly to get the best possible school supplies for them since most cannot afford them. Liz and Andy are constantly in cou",
    gender: "Female",
    age_min: 30,
    age_max: 40,
    ethnicities: [],
    is_open: true,
    project: projects[18],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(4),
  },
  {
    id: "r65",
    project_id: "p20",
    name: "CONNOR MACLEOD",
    role_type: "Leading",
    description: "Attached: HENRY CAVILL He is a ruggedly handsome Highlander who upon an unsuspected battle realizes that he has an unholy connection with another, the Kurgan. After fighting the Kurgan, he is killed. Only he isn’t dead, he comes back to life and is run out of ",
    gender: "Male",
    age_min: 25,
    age_max: 30,
    ethnicities: ["Caucasian"],
    is_open: true,
    project: projects[19],
    candidate_count: 0,
    submission_state: "submitted",
    matched_at: daysAgo(5),
  },
  {
    id: "r66",
    project_id: "p20",
    name: "RAMIREZ",
    role_type: "Supporting",
    description: "Set: Russell Crowe A dashing older gentleman. He gives Macleod the same feeling of connection and bloodlust as he did with the Kurgan, yet Ramirez acts as a mentor to Macleod, exposing him to his true nature as an immortal and helping him to train for the fina",
    gender: "Male",
    age_min: 40,
    age_max: 60,
    ethnicities: ["Diverse"],
    is_open: true,
    project: projects[19],
    candidate_count: 0,
    submission_state: "audition",
    matched_at: daysAgo(6),
  },
  {
    id: "r67",
    project_id: "p20",
    name: "HEATHER",
    role_type: "Supporting",
    description: "Set: Karen Gillan She is an ethereal and stoic beauty. Macleod's first love, who leaves their clan with him after he's exiled for surviving a knife through the heart during his first run in with The Kurgan. Heather is who grounds Macleod -- she is the guiding ",
    gender: "Female",
    age_min: 20,
    age_max: 25,
    ethnicities: [],
    is_open: true,
    project: projects[19],
    candidate_count: 0,
    submission_state: null,
    matched_at: daysAgo(7),
  },
  {
    id: "r68",
    project_id: "p20",
    name: "FASIL",
    role_type: "Supporting",
    description: "Set: NASSIM LYES He was one of the last of the remaining Immortals at the time of the Gathering. Before arriving in New York, he had been living in Syria.",
    gender: "Male",
    age_min: 25,
    age_max: 30,
    ethnicities: [],
    is_open: true,
    project: projects[19],
    candidate_count: 0,
    submission_state: "callback",
    matched_at: daysAgo(8),
  },
];

const clients: Client[] = [
  {
    id: "c1",
    first_name: "Ava",
    last_name: "Sinclair",
    email: "ava@example.com",
    phone: "+1 310 555 0142",
    agency: "Northlight Management",
    representation_type: "Manager",
    ethnicity: "Open",
    // One client with a picture so the image and initials paths are both
    // visible while running on stubs.
    avatar_url:
      "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='64' height='64'%3E%3Crect width='64' height='64' fill='%23334155'/%3E%3Ccircle cx='32' cy='25' r='11' fill='%2394A3B8'/%3E%3Cpath d='M8 64c0-13 11-21 24-21s24 8 24 21z' fill='%2394A3B8'/%3E%3C/svg%3E",
    filters: {
      ...EMPTY_FILTERS,
      gender: "Female",
      age_min: 30,
      age_max: 45,
      preferred_genres: ["Drama", "Thriller"],
      film_tv_preference: "Both",
    },
    created_at: "2026-08-14T09:12:00Z",
  },
  {
    id: "c2",
    first_name: "Priya",
    last_name: "Raman",
    email: "priya@example.com",
    phone: null,
    agency: "Northlight Management",
    representation_type: "Manager",
    ethnicity: "South Asian",
    avatar_url: null,
    filters: {
      ...EMPTY_FILTERS,
      gender: "Female",
      age_min: 22,
      age_max: 32,
      preferred_genres: ["Comedy"],
      film_tv_preference: "TV",
    },
    created_at: "2026-08-20T15:40:00Z",
  },
  {
    id: "c3",
    // Narrow filters that only the sparsest role survives, which is the
    // open question below: a role with no gender and no age recorded
    // matches every client. His Tracking list is the empty state.
    first_name: "Desmond",
    last_name: "Okafor",
    email: null,
    phone: null,
    agency: null,
    representation_type: "Co-manager",
    ethnicity: "Black",
    avatar_url: null,
    filters: { ...EMPTY_FILTERS, gender: "Male", age_min: 60, age_max: 75 },
    created_at: "2026-08-27T11:02:00Z",
  },
];

/** Who the stub feed attributes a logged call to. Comes from the signed in
 *  user once the updates feed is served by the API. */
const MANAGER = "Sarah";

/** Without seen, which is derived on read rather than stored on the row.
 *  Read state lives in seenProjects and is keyed on a client and a
 *  production, the same as the API keys it, so a fixture carrying its own
 *  copy would be a second answer able to disagree with the match list. */
const updates: Omit<ProjectUpdate, "seen" | "clients">[] = [
  {
    id: "u1",
    project_id: "p1",
    body: "Casting director updated on American Psycho Remake, now Jessica Ronane.",
    detail: "American Psycho Remake • Lionsgate",
    kind: "field_changed",
    occurred_at: hoursAgo(2),
  },
  {
    id: "u2",
    project_id: "p3",
    body: "Bell Jar, The moved to Features, New in the latest IE grid.",
    detail: "Bell Jar, The • Focus Features",
    kind: "status_changed",
    occurred_at: hoursAgo(5),
  },
  {
    id: "u3",
    project_id: "p2",
    body: "Shooting location on AMERICAN PSYCHO could not be read with confidence from the latest grid.",
    detail: "Needs a check • WME, page 12",
    kind: "needs_review",
    occurred_at: hoursAgo(19),
  },
  {
    id: "u4",
    project_id: "p7",
    body: "Start date on 6B1B moved to August 4, 2026, which conflicts with a value a manager entered.",
    detail: "6B1B • conflicts with a manager entry",
    kind: "conflict",
    occurred_at: hoursAgo(28),
  },
  {
    id: "u5",
    project_id: "p2",
    body: "AMERICAN PSYCHO is now Active in the WME grid.",
    detail: "AMERICAN PSYCHO • Lionsgate-Summit",
    kind: "status_changed",
    occurred_at: hoursAgo(50),
  },
  {
    id: "u6",
    project_id: "p3",
    body: "Sarah Polley attached as director on Bell Jar, The.",
    detail: "Bell Jar, The • director",
    kind: "attachment",
    occurred_at: hoursAgo(8),
  },
  {
    id: "u7",
    project_id: "p1",
    body: "American Psycho Remake starts shooting on September 1, 2026.",
    detail: "American Psycho Remake • shoot date coming up",
    kind: "deadline",
    occurred_at: hoursAgo(26),
  },
];

/**
 * Who has starred what, keyed on the client as well as the role.
 *
 * It was a set of role ids, which made a star a fact about the role rather
 * than about a client, and the API has never worked that way: a star is a row
 * joining a role to one client's coverage. The same part is worth following
 * for one person and not another, which is the whole reason the endpoint
 * carries the client in its path.
 *
 * What the old shape did on screen: starring a role for Ava made it appear
 * starred on the tracking list of every other client whose filters happened to
 * match it. Nobody noticed because the tracking lists are short on fixtures.
 *
 * Seeded so the dashboard still has something to show, including one
 * production two clients both follow, which is the case worth having and the
 * one that is easiest to get wrong.
 */
const tracked = new Set<string>([
  "c1:r1",
  "c1:r5",
  "c2:r5",
  "c2:r6",
  "c1:r13",
  "c2:r27",
  "c1:r41",
]);

const trackKey = (clientId: string, roleId: string) => `${clientId}:${roleId}`;

/** The clients following a part on this production, which is where an
 *  update's client list comes from on the API too. */
function clientsFollowing(projectId: string): { client_id: string; name: string }[] {
  const starring = new Set(
    fixtures
      .filter((role) => role.project_id === projectId)
      .flatMap((role) =>
        clients.filter((c) => tracked.has(trackKey(c.id, role.id))).map((c) => c.id),
      ),
  );
  return clients
    .filter((c) => starring.has(c.id))
    .map((c) => ({ client_id: c.id, name: `${c.first_name} ${c.last_name}`.trim() }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Productions this manager has marked read, held the same way tracking is.
 *
 * Keyed on the production rather than the role, which is what the API does,
 * so a fixture run and a live run answer the same way when one production
 * carries several parts.
 */
/** Every tag on a production that has a part, with its kind and a count. */
function statusOptions(): StatusOption[] {
  const counted = new Map<string, { label: string; projects: Set<string> }>();
  for (const role of roles()) {
    for (const tag of statusTags(role.project.status_raw)) {
      const folded = tag.toLowerCase();
      const held = counted.get(folded) ?? { label: tag, projects: new Set<string>() };
      held.projects.add(role.project.id);
      counted.set(folded, held);
    }
  }
  return [...counted.entries()]
    .sort(([a], [b]) => {
      const at = STATUS_ORDER.indexOf(a);
      const bt = STATUS_ORDER.indexOf(b);
      return (at === -1 ? STATUS_ORDER.length : at) - (bt === -1 ? STATUS_ORDER.length : bt)
        || a.localeCompare(b);
    })
    .map(([folded, held]) => ({
      label: held.label,
      kind: STATUS_KIND[folded] ?? "unknown",
      productions: held.projects.size,
    }));
}

/** Questions the way the log records them. One from before tool results
 *  were kept, so the screen's "not recorded" state is visible on fixtures. */
const assistantTurns: AssistantTurn[] = [
  {
    id: "t3",
    asked_at: hoursAgo(1),
    asked_by: "Paul Sun",
    question: "Show me dramas at Warner Brothers",
    reply: "Nothing matched dramas at Warner Brothers. Of the 16 Warner productions, only 4 have a genre recorded, so this may be missing rather than absent.",
    tools: [
      {
        tool: "find_productions",
        arguments: { studio: "Warner Brothers", genre: "Drama" },
        result:
          "Nothing matches studio 'Warner Brothers', genre 'Drama'. Of the 16 productions matching studio 'Warner Brothers', only 4 have a genre recorded, so this may be missing rather than absent.",
      },
    ],
    citations: [],
    model: "gpt-6-sol",
    rounds: 2,
    wrote: false,
  },
  {
    id: "t2",
    asked_at: hoursAgo(3),
    asked_by: "Paul Sun",
    question: "Show me projects Carmen Cuba is casting",
    reply: "Carmen Cuba is credited on two projects, both marked actively casting.",
    tools: [
      {
        tool: "find_productions",
        arguments: { person: "Carmen Cuba" },
        result: `2 productions match person 'Carmen Cuba':
- ALONE AT DAWN | Amazon Studios
- BEHEMOTH! | Searchlight Pictures`,
      },
    ],
    citations: [
      { kind: "project", id: "p3", label: "ALONE AT DAWN", detail: "Amazon Studios" },
      { kind: "project", id: "p4", label: "BEHEMOTH!", detail: "Searchlight Pictures" },
    ],
    model: "gpt-6-sol",
    rounds: 2,
    wrote: false,
  },
  {
    id: "t1",
    asked_at: hoursAgo(30),
    asked_by: "Paul Sun",
    question: "Show me all Taylor Sheridan projects",
    reply: "Nothing matched Taylor Sheridan in the current production grid.",
    tools: [{ tool: "find_productions", arguments: { person: "Taylor Sheridan" }, result: null }],
    citations: [],
    model: "gpt-6-sol",
    rounds: 2,
    wrote: false,
  },
];

/** Names on a fixture production that are in an address book, one of them by
 *  nickname, so both states show on the project page. */
const knownContactsByProject: Record<string, KnownContact[]> = {
  p1: [
    {
      person: "Jessica Ronane",
      capacities: ["casting_director"],
      contact_name: "Jessica Ronane",
      mailbox: "psun@1v1ent.com",
      match: "exact",
      emails: ["jessica@ronanecasting.example"],
      phones: [{ kind: "mobile", number: "+1 310 555 0144" }],
    },
    {
      person: "Michael Grant",
      capacities: ["producer"],
      contact_name: "Mike Grant",
      mailbox: "psun@1v1ent.com",
      match: "nickname",
    },
  ],
};

const seenProjects = new Set<string>();

/** Keyed the way the API keys it, client and production together, so a fixture
 *  run and a live run answer the same when one production is on two lists. */
const seenKey = (clientId: string, projectId: string) => `${clientId}:${projectId}`;

/** The fixtures as the API would send them, read state and all. */
function roles(clientId?: string): RoleWithProject[] {
  return fixtures.map((role) => ({
    ...role,
    // The fixtures carry only the printed wording; the live API also sends the
    // controlled key, so the stub derives one so a tier sort has it here too.
    role_type_key: roleTypeKeyOf(role.role_type),
    seen: clientId ? seenProjects.has(seenKey(clientId, role.project_id)) : null,
  }));
}

/**
 * A grid's role wording as its controlled key, lowercased with the spaces
 * turned to underscores. The live API carries a real role_type_key per part;
 * the fixtures do not, so the stub derives one so its Role Type picker and its
 * query filter on the same value.
 */
function roleTypeKeyOf(roleType: string | null): string {
  return (roleType ?? "").trim().toLowerCase().replace(/\s+/g, "_");
}

function matchesRoleType(role: RoleWithProject, roleType: string | string[] | null): boolean {
  const wanted = Array.isArray(roleType) ? roleType : roleType ? [roleType] : [];
  if (wanted.length === 0 || !role.role_type) return true;
  const have = role.role_type.trim().toLowerCase();
  return wanted.some((rt) => rt.trim().toLowerCase() === have);
}

function genreParts(project: Project): string[] {
  const source = project.genres?.length ? project.genres : project.genre ? [project.genre] : [];
  return source.flatMap((value) =>
    value
      .split(/[,/]/)
      .map((part) => part.trim())
      .filter(Boolean),
  );
}

function matchesGenre(role: RoleWithProject, preferred: string[]): boolean {
  if (preferred.length === 0) return true;
  const genres = genreParts(role.project).map((part) => part.toLowerCase());
  if (genres.length === 0) return true;
  return preferred.some((genre) => genres.includes(genre.toLowerCase()));
}

function matchesFilmTvPreference(role: RoleWithProject, preference: string | null): boolean {
  if (!preference || preference === "Both" || !role.project.project_type) return true;
  const kind = role.project.project_type.toLowerCase();
  const isTv = /\b(series|episodic|television|tv)\b/.test(kind);
  const isFilm = /\bfilm|feature\b/.test(kind);
  if (preference === "TV") return isTv || !isFilm;
  if (preference === "Film") return isFilm || !isTv;
  return true;
}

function matches(role: RoleWithProject, client: Client): boolean {
  const f = client.filters;
  if (!role.is_open) return false;
  if (!matchesRoleType(role, f.role_type)) return false;
  if (f.gender && role.gender && role.gender !== f.gender) return false;
  // An unknown value is not treated as a mismatch, because dropping roles
  // for missing data is how a manager stops trusting the list. The cost is
  // that a role with nothing filled in matches everyone. Worth a decision
  // with a manager rather than picking one silently.
  if (f.age_min !== null && role.age_max !== null && role.age_max < f.age_min) {
    return false;
  }
  if (f.age_max !== null && role.age_min !== null && role.age_min > f.age_max) {
    return false;
  }
  if (!matchesGenre(role, f.preferred_genres)) return false;
  if (!matchesFilmTvPreference(role, f.film_tv_preference)) return false;
  return true;
}

function matchesSearch(role: RoleWithProject, q: string | undefined): boolean {
  const query = q?.trim().toLowerCase();
  if (!query) return true;
  return [
    role.name,
    role.role_type,
    role.project.title,
    role.project.studio,
    role.project.network,
    role.project.project_type,
    role.project.location,
    role.project.status_raw,
    role.project.genre,
    role.project.directors.join(" "),
    role.project.producers.join(" "),
  ].some((value) => value?.toLowerCase().includes(query));
}

/**
 * New means unread, the same as the API means it.
 *
 * It used to mean matched within two days, read off a date the grids almost
 * never carry, so the count was nearly always zero. The fixtures answering one
 * way and the API the other would have made this the last place the old rule
 * survived, which is the sort of thing that only shows up in a demo.
 */
function isNewMatch(role: RoleWithProject): boolean {
  return !role.seen;
}

const delay = <T,>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), 160));

export const stubSource: DataSource = {
  listClients: () => delay([...clients]),
  getClient: (id) => delay(clients.find((c) => c.id === id) ?? null),
  addClient: (input: NewClient) => {
    const created: Client = {
      id: `c${clients.length + 1}`,
      created_at: new Date().toISOString(),
      ...input,
    };
    clients.push(created);
    return delay(created);
  },
  updateClient: (id, input: NewClient) => {
    const at = clients.findIndex((c) => c.id === id);
    if (at === -1) return Promise.reject(new Error(`No client with id ${id}.`));
    const updated: Client = {
      ...clients[at],
      ...input,
      filters: {
        ...EMPTY_FILTERS,
        ...input.filters,
        preferred_genres: [...input.filters.preferred_genres],
      },
    };
    clients[at] = updated;
    return delay(updated);
  },
  deleteClient: (id) => {
    const at = clients.findIndex((c) => c.id === id);
    if (at !== -1) clients.splice(at, 1);
    return delay(undefined);
  },
  listMatchedRoles: (clientId, options = {}) => {
    const client = clients.find((c) => c.id === clientId);
    const limit = Math.max(1, options.limit ?? 50);
    const offset = Math.max(0, options.offset ?? 0);
    const matched = client
      ? roles(clientId)
          .filter((r) => matches(r, client))
          .filter((r) => matchesSearch(r, options.q))
      : [];
    // The read filter narrows the list but not the count, the same way the
    // API does it. Showing "0 unread" over a list filtered to the read ones
    // would be true of what is on screen and wrong about what is left.
    const shown =
      options.read === undefined
        ? matched
        : matched.filter((r) => (r.seen === true) === options.read);
    const items = shown.slice(offset, offset + limit);
    const page: MatchedRolesPage = {
      items,
      total_count: shown.length,
      limit,
      offset,
      has_more: offset + items.length < shown.length,
      capped: false,
      new_count: matched.filter(isNewMatch).length,
    };
    return delay(page);
  },
  listTrackedRoles: (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    if (!client) return delay([]);
    // Whether this client starred it, not whether their filters happen to
    // match it. A star outlives the filters: 1v1 keep following a part after
    // the criteria that surfaced it stop matching, which is the point of the
    // tracking list being separate from the match list.
    return delay(roles(clientId).filter((r) => tracked.has(trackKey(clientId, r.id))));
  },
  listAllTrackedRoles: () =>
    delay(
      roles()
        .filter((r) => clients.some((c) => tracked.has(trackKey(c.id, r.id))))
        .map(
          (role): FollowedRole => ({
            role_id: role.id,
            clients: clients
              .filter((client) => tracked.has(trackKey(client.id, role.id)))
              .map((client) => ({
                client_id: client.id,
                name: `${client.first_name} ${client.last_name}`.trim(),
              })),
          }),
        )
        .filter((role) => role.clients.length > 0),
    ),
  listRoles: (options: ListRolesOptions = {}) => {
    const limit = Math.max(1, options.limit ?? 50);
    const offset = Math.max(0, options.offset ?? 0);
    const cutoff = options.added_within_days;
    const filtered = roles().filter((r) => {
      if (options.role_type && r.role_type !== options.role_type) return false;
      if (options.role_type_key?.length) {
        if (!options.role_type_key.includes(roleTypeKeyOf(r.role_type))) return false;
      }
      if (options.project_type && r.project.project_type !== options.project_type) return false;
      if (options.gender && r.gender !== options.gender) return false;
      if (options.location && r.project.location !== options.location) return false;
      // Per tag, and the whole cell as a fallback, exactly as the API does
      // it. status_raw is a comma joined list, so comparing the whole cell
      // meant picking Casting found only productions whose cell was the one
      // word, which on live was none of them. Mirrored here because a screen
      // checked against fixtures would otherwise look broken while the real
      // one worked, which is the wrong way round to find out.
      if (options.status) {
        const wanted = options.status.trim().toLowerCase();
        const carries = statusTags(r.project.status_raw).some(
          (tag) => tag.toLowerCase() === wanted,
        );
        if (!carries && r.project.status_raw !== options.status) return false;
      }
      if (cutoff) {
        const at = r.matched_at ? Date.parse(r.matched_at) : Number.NaN;
        if (Number.isNaN(at)) return false;
        if ((Date.now() - at) / 86_400_000 > cutoff) return false;
      }
      return matchesSearch(r, options.q);
    });
    const items = filtered.slice(offset, offset + limit);
    const page: RolesPage = {
      items,
      total_count: filtered.length,
      limit,
      offset,
      has_more: offset + items.length < filtered.length,
    };
    return delay(page);
  },
  listRoleOptions: () => {
    // Tags, not whole cells, which is what the API returns. The fixtures used
    // to return every distinct status cell, so the picker here showed the same
    // unusable list the live one did before it was fixed, and a screen checked
    // against fixtures would have looked right while the real one was not.
    const uniq = (values: (string | null)[]) =>
      [...new Set(values.filter(Boolean) as string[])].sort();
    const options: RoleOptions = {
      role_types: uniq(roles().map((r) => r.role_type)),
      // The fixtures carry only the grid's wording, so the keys are derived
      // from it the same way the filter does below, keeping the stubbed picker
      // and the stubbed query in step the way the live pair is.
      role_type_keys: uniq(roles().map((r) => roleTypeKeyOf(r.role_type))),
      project_types: uniq(roles().map((r) => r.project.project_type)),
      genders: uniq(roles().map((r) => r.gender)),
      locations: uniq(roles().map((r) => r.project.location)),
      statuses: statusOptions().filter((o) => o.kind === "stage").map((o) => o.label),
      status_options: statusOptions(),
    };
    return delay(options);
  },
  listGenres: () =>
    delay(
      [...new Set(projects.flatMap(genreParts))]
        .sort((a, b) => a.localeCompare(b)),
    ),
  listProjectRoles: (projectId) =>
    // Open roles first, then by name, so the ones still castable are the ones
    // a manager reads first.
    delay(
      roles()
        .filter((r) => r.project.id === projectId)
        .sort((a, b) =>
          a.is_open === b.is_open ? a.name.localeCompare(b.name) : a.is_open ? -1 : 1,
        ),
    ),
  getRole: (id) => delay(roles().find((r) => r.id === id) ?? null),
  listUpdates: (clientId) => {
    // Derived from who has starred a part on the production, which is how the
    // API decides it. The fixtures used to carry a hand written client list on
    // each update, so the names on screen had no relationship to the stars and
    // starring something changed nothing on the dashboard.
    const withClients = updates.map((u) => ({
      ...u,
      clients: clientsFollowing(u.project_id),
    }));
    const shown = clientId
      ? withClients.filter((u) => u.clients.some((c) => c.client_id === clientId))
      : withClients;
    return delay(
      shown.map((u) => {
        // Per client first, then the row's own answer from those, which is
        // the order the API does it in. Grouped by client the dashboard reads
        // the per client one, so a fixture that only carried the aggregate
        // would have looked right ungrouped and wrong grouped.
        const clients = u.clients.map((c) => ({
          ...c,
          seen: seenProjects.has(seenKey(c.client_id, u.project_id)),
        }));
        return {
          ...u,
          clients,
          // null where nobody is following a part on it: there is no client
          // to have read it for.
          seen: clients.length ? clients.every((c) => c.seen) : null,
        };
      }),
    );
  },
  listCalls: (projectId) =>
    delay(
      calls
        .filter((c) => c.project_id === projectId)
        .sort((a, b) => +new Date(b.occurred_at) - +new Date(a.occurred_at)),
    ),
  addCall: (entry) => {
    const created: CallLogEntry = { id: `cl${calls.length + 1}`, ...entry };
    calls.unshift(created);

    // Logging a call tells everyone following the project. The update is
    // raised here rather than by whichever screen logged it, so the call
    // form and the assistant both produce it and neither can forget to.
    const project = projects.find((p) => p.id === entry.project_id);
    if (project) {
      const who = entry.contact.replace(/\s*\(.*\)$/, "");
      updates.unshift({
        id: `u${updates.length + 1}`,
        project_id: project.id,
        body: `${MANAGER} logged ${entry.kind === "Email" ? "an email" : "a call"} with ${who} on ${project.title}.`,
        detail: entry.follow_up
          ? `Follow up: ${entry.follow_up}`
          : "Everyone following this project has been told",
        kind: "call_logged",
        occurred_at: entry.occurred_at,
      });
    }
    return delay(created);
  },
  // The client is accepted and ignored. The fixture set is one flat list of
  // role ids rather than one per client, which is wrong in the same way the
  // old signature was, and is left alone because these rows go away the day
  // the matching area reads the API.
  setTracked: (clientId, roleId, on) => {
    if (on) tracked.add(trackKey(clientId, roleId));
    else tracked.delete(trackKey(clientId, roleId));
    return delay(undefined);
  },
  /**
   * No model behind the fixtures, and that is the honest answer rather than a
   * limitation. With this area off the panel answers by matching over the
   * productions it has loaded, which is what it has always done.
   */
  chatStatus: () => delay({ connected: false, model: null }),
  listAssistantTurns: (options = {}) => {
    const q = (options.q ?? "").trim().toLowerCase();
    const limit = options.limit ?? 50;
    const offset = options.offset ?? 0;
    const shown = assistantTurns.filter((t) => !q || t.question.toLowerCase().includes(q));
    return delay({
      items: shown.slice(offset, offset + limit),
      total_count: shown.length,
      limit,
      offset,
    });
  },
  listRoleCandidates: (roleId) => {
    const candidates = [
      { name: "Olivia Homan", standing: "offer", standing_label: "Offer" },
      { name: "Daniel Kaluuya", standing: "attached", standing_label: "Attached" },
      { name: "Zazie Beetz", standing: "attached", standing_label: "Attached" },
      { name: "John Boyega", standing: "suggested", standing_label: "Suggested" },
      { name: "Letitia Wright", standing: "passed", standing_label: "Passed" },
    ];
    // Also the people the production itself carries, so searching one of them
    // resolves to this role the way the live per-role candidate list lets it.
    // The fixed names above are kept so the section still reads as a full one.
    const role = roles().find((r) => r.id === roleId);
    const have = new Set(candidates.map((c) => c.name.toLowerCase()));
    for (const name of role?.project.key_people ?? []) {
      if (!have.has(name.toLowerCase())) {
        candidates.push({ name, standing: "suggested", standing_label: "Suggested" });
      }
    }
    return delay(candidates);
  },
  listKnownContacts: (projectId) => delay(knownContactsByProject[projectId] ?? []),
  askAssistant: () => {
    throw new Error("The assistant is not connected to a model here.");
  },
  /**
   * Stand in for the backend resolver with a readable proposal.
   *
   * The real decision, new production or an amendment to one we hold, is
   * scored on several signals on the server. This reads the obvious "Key:
   * value" lines a pasted email carries, matches the title against the
   * fixtures the same loose way the panel's own collapse does, and returns a
   * proposal in the same shape the endpoint will. Enough for the confirm step
   * to be built and seen working before the route exists, and nothing like
   * the real resolution.
   */
  proposeProjectChange: (text) => {
    // Split on new lines, and also on a comma that comes right before a
    // "Key:", so a one line message survives the single line composer
    // collapsing an email's new lines to spaces: "Update project X, Studio:
    // Y, Cast: Z" breaks into its parts, while "Cast: Z, W" keeps both names
    // because that comma is not in front of a label.
    const segments = text
      .split(/\r?\n|;|,(?=\s*[A-Za-z][A-Za-z ]{0,20}:)/)
      .map((s) => s.trim())
      .filter(Boolean);

    const field = (keys: RegExp): string | null => {
      for (const seg of segments) {
        const m = seg.match(/^([^:]{1,32}):\s*(.+)$/);
        if (m && keys.test(m[1].trim())) return m[2].trim();
      }
      return null;
    };

    const title =
      field(/^(title|project|production)$/i) ??
      (segments[0] ?? "")
        .replace(/^(new|add|update|create)\s+(a\s+)?(project|production)[:\-\s]*/i, "")
        .trim() ??
      "Untitled production";
    const studio = field(/^(studio|network)$/i);
    const type = field(/^(type|format)$/i);
    const start = field(/^(start|start date|shoot|dates?)$/i);

    const roles: ProposedRole[] = segments
      .filter((l) => /^(role|cast|add role)s?\s*:/i.test(l))
      .flatMap((l) =>
        l
          .replace(/^(role|cast|add role)s?\s*:/i, "")
          .split(/[;,]/)
          .map((piece) => piece.trim())
          .filter(Boolean)
          .map((piece) => {
            const m = piece.match(/^(.*?)\s*[([-]\s*([^)]+?)\)?$/);
            return m
              ? { name: m[1].trim(), standing: m[2].trim().toLowerCase() }
              : { name: piece, standing: null };
          }),
      );

    const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
    const wanted = norm(title);
    const exact = projects.find((p) => norm(p.title) === wanted);
    const near =
      exact ??
      projects.find((p) => {
        const have = norm(p.title);
        return have && wanted && (have.includes(wanted) || wanted.includes(have));
      });

    const target = exact ?? near;
    const fieldsFor = (p?: Project): ProposedFieldChange[] => {
      const out: ProposedFieldChange[] = [];
      const add = (label: string, after: string | null, before: string | null) => {
        if (after) out.push({ field: label, after, before: before ?? null });
      };
      add("Studio", studio, p?.studio ?? p?.network ?? null);
      add("Type", type, p?.project_type ?? null);
      add("Start date", start, null);
      return out;
    };

    let change: ProposedChange;
    if (exact) {
      change = {
        kind: "update",
        project_id: exact.id,
        project_title: exact.title,
        fields: fieldsFor(exact),
        roles,
        alternatives: [],
        source_text: text,
      };
    } else if (near) {
      // Every fixture whose title overlaps the paste, best first, so the review
      // offers the near productions to pick between the way the backend does.
      const overlapping = [
        near,
        ...projects.filter((p) => {
          const have = norm(p.title);
          return p !== near && have && wanted && (have.includes(wanted) || wanted.includes(have));
        }),
      ];
      change = {
        kind: "review",
        project_id: near.id,
        project_title: near.title,
        fields: fieldsFor(near),
        roles,
        alternatives: overlapping.map((p, i) => ({
          project_id: p.id,
          project_title: p.title,
          score: i === 0 ? 0.6 : 0.45,
          fields: fieldsFor(p),
        })),
        source_text: text,
      };
    } else {
      change = {
        kind: "new",
        project_id: null,
        project_title: title,
        fields: fieldsFor(undefined),
        roles,
        alternatives: [],
        source_text: text,
      };
    }
    // Silence the unused warning when nothing matched; target documents intent.
    void target;
    return delay(change);
  },
  /**
   * Off the backend this writes nothing real, but it raises the same feed row
   * a confirmed change would, so the "everyone following it has been told"
   * half of the flow can be seen working too.
   */
  applyProjectChange: (change) => {
    const projectId = change.project_id ?? `new-${Date.now()}`;
    const now = new Date().toISOString();

    // The change itself, the same project_added or project_updated row the
    // feed draws for a confirmed change.
    if (change.fields.length > 0 || change.roles.length === 0) {
      updates.unshift({
        id: `u${updates.length + 1}`,
        project_id: projectId,
        body:
          change.kind === "new"
            ? `${MANAGER} added ${change.project_title}.`
            : `${MANAGER} updated ${change.project_title}.`,
        detail: "Everyone following this production has been told",
        kind: change.kind === "new" ? "project_added" : "project_updated",
        occurred_at: now,
      });
    }

    // Each added role as its own new-role-to-consider row, mirroring the
    // backend, so the people following the production see a part to act on.
    for (const role of change.roles) {
      const standing = role.standing ? ` (${role.standing})` : "";
      updates.unshift({
        id: `u${updates.length + 1}`,
        project_id: projectId,
        body: `A new role to consider on ${change.project_title}: ${role.name}${standing}.`,
        detail: "Everyone following this production has been told",
        kind: "new_role",
        occurred_at: now,
      });
    }
    return delay(undefined);
  },
  setSeen: (clientId, projectId, seen) => {
    if (seen) seenProjects.add(seenKey(clientId, projectId));
    else seenProjects.delete(seenKey(clientId, projectId));
    return delay(undefined);
  },
  markListSeen: (clientId, seen, q) => {
    const client = clients.find((c) => c.id === clientId);
    const onList = client
      ? roles(clientId).filter((r) => matches(r, client) && matchesSearch(r, q))
      : [];
    // Productions rather than roles, and counted the same way the API counts
    // them: how many were on the list, and how many actually changed.
    const projectIds = [...new Set(onList.map((r) => r.project.id))];
    const changed = projectIds.filter(
      (id) => seenProjects.has(seenKey(clientId, id)) !== seen,
    ).length;
    for (const id of projectIds) {
      if (seen) seenProjects.add(seenKey(clientId, id));
      else seenProjects.delete(seenKey(clientId, id));
    }
    return delay({ projects: projectIds.length, changed });
  },
  uploadGrid: (file, agency) => {
    // A re-send of the same name reads as the duplicate the API would report,
    // so the screen's two outcomes can both be seen without the real backend.
    const duplicate = uploadedNames.has(file.name);
    uploadedNames.add(file.name);
    return delay<GridUpload>({
      document_id: crypto.randomUUID(),
      filename: file.name,
      source_agency: agency,
      status: "uploaded",
      storage_key: `uploads/${agency}/${file.name}`,
      duplicate,
    });
  },
};

const uploadedNames = new Set<string>();

export const isTracked = (clientId: string, roleId: string): boolean =>
  tracked.has(trackKey(clientId, roleId));
